import { Centered, Loading, MaterialIcon, useLoading } from '@gorila-shared-ui/components';
import { Block } from 'baseui/block';
import { Button } from 'baseui/button';
import { HeadingXSmall, ParagraphSmall } from 'baseui/typography';
import { useEffect, useState } from 'react';
import { useStyles } from '../../../../hooks/useStyles';
import { getS3FileUrlByKey } from '../../../../services/appService';
import { ChannelId } from '../../../../types/gptTypes';
import { StreamingCaptureChannelResponse } from '../../../../types/streaming';
import StreamingChannelTag from '../StreamingChannelTag';

type Props = {
  data: StreamingCaptureChannelResponse;
  channelId: ChannelId;
  onPreview: (url: string) => void;
};

const StreamingCapturePreview = ({ data, channelId, onPreview }: Readonly<Props>) => {
  const { css, theme } = useStyles();
  const { loading, startLoading, stopLoading } = useLoading(!!data.key);
  const [imageUrl, setImageUrl] = useState('');
  const [imageError, setImageError] = useState('');
  const imageWrapperStyles = css({
    position: 'relative',
    height: '200px',
    minWidth: '220px',
    width: '100%',
    background: theme.colors.backgroundInversePrimary,
    borderRadius: theme.borders.radius300,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.sizing.scale100,
  });
  const imageStyles = css({
    height: '100%',
    maxWidth: '100%',
    objectFit: 'cover',
  });
  const fullScreenStyles = css({
    position: 'absolute',
    bottom: theme.sizing.scale200,
    right: theme.sizing.scale200,
  });
  const hasError = !!data.error || imageError.length > 0;

  useEffect(() => {
    return () => {
      setImageError('');
    };
  }, [data, channelId]);

  useEffect(() => {
    if (!data.key) return;
    const loadImage = async () => {
      if (!data.key) return;
      startLoading();
      const { url, error } = await getS3FileUrlByKey(data.key);
      if (error) {
        setImageError(typeof error === 'string' ? error : error.join(', '));
      } else if (url) {
        setImageUrl(url);
      }
      stopLoading();
    };

    loadImage();
  }, [data.key]);

  return (
    <div className={imageWrapperStyles}>
      {hasError && (
        <>
          <HeadingXSmall
            margin={0}
            color={theme.colors.contentInversePrimary}
          >
            Error
          </HeadingXSmall>
          <ParagraphSmall
            margin={0}
            color={theme.colors.contentInversePrimary}
            paddingLeft={theme.sizing.scale500}
            paddingRight={theme.sizing.scale500}
            style={{
              textAlign: 'center',
            }}
          >
            {data.error || imageError}
          </ParagraphSmall>
        </>
      )}
      {!hasError && (
        <>
          {loading && (
            <Centered>
              <Loading size="compact" />
            </Centered>
          )}
          {!loading && (
            <>
              <img
                src={imageUrl}
                className={imageStyles}
                alt="capture_image"
              />
              <Button
                size="mini"
                kind="tertiary"
                shape="square"
                className={fullScreenStyles}
                colors={{ backgroundColor: 'transparent', color: theme.colors.contentInversePrimary }}
                onClick={() => {
                  onPreview(imageUrl);
                }}
              >
                <MaterialIcon
                  name="aspect_ratio"
                  color={theme.colors.contentInversePrimary}
                />
              </Button>
            </>
          )}
        </>
      )}
      {!loading && <CaptureLayer channelId={channelId} />}
    </div>
  );
};

const CaptureLayer = ({ channelId }: { channelId: ChannelId }) => {
  const { theme, css } = useStyles();
  const videoLayerStyles = css({
    position: 'absolute',
    top: theme.sizing.scale200,
    left: theme.sizing.scale200,
    color: theme.colors.contentInversePrimary,
  });
  return (
    <Block className={videoLayerStyles}>
      <StreamingChannelTag channelId={channelId} />
    </Block>
  );
};

export default StreamingCapturePreview;
